import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { withLoadResilience } from './loadResilience';
import type {
  RestaurantConfiguration,
  RestaurantAnnouncement,
  AnnouncementTranslations,
  MarqueeTranslations,
  RestaurantHero,
  RestaurantPromo,
  DeliveryPartner,
  Promotion,
  PromotionAppliesTo,
  Menu,
  CatalogTranslations,
  Category,
  MenuItem,
  OptionGroup,
  OptionChoice,
  CartOptionSaleMode,
  OrderMode,
  DineInCourse,
} from '@/types';
import { parseProductAllergens } from '@/features/menu/allergens/allergens';
import type { Period } from '@/features/menu/servicePeriod';
import { renderTiptapToHtml } from '@/features/announcement/renderTiptap';
import type { AmbientSource } from '@/features/ambient/useAmbientAudio';

interface RawActiveAnnouncement {
  uuid: string;
  headline?: string;
  body_doc?: unknown;
  cta_label?: string | null;
  cta_href?: string | null;
  translations?: AnnouncementTranslations | null;
}

interface RawConfiguration {
  enable_emporter: boolean;
  enable_livraison: boolean;
  enable_reservation: boolean;
  enable_sur_place?: boolean;
  prep_time_emporter_min: number;
  prep_time_livraison_min: number;
  active_announcement: RawActiveAnnouncement | null;
  marquee_messages: string[];
  marquee_messages_translations?: MarqueeTranslations | null;
  ambient_preset: string;
  delivery_partners: DeliveryPartner[];
  promotions?: unknown[];
  hero: Partial<RestaurantHero> | null;
  promo: Partial<RestaurantPromo> | null;
  payment_online_enabled?: boolean;
  payment_online_description?: string;
  payment_onsite_enabled?: boolean;
  payment_onsite_description?: string;
  // SC-554 — montant minimum de commande, chaine decimale ('0.00' = aucun).
  min_order_amount_emporter?: string;
  min_order_amount_livraison?: string;
  // SC-902 — « Envoi par service ». Optionnels : un resource_api anterieur au
  // champ ne les envoie pas, ce qui doit se comporter comme l'option eteinte
  // (design §1.2).
  dine_in_courses_enabled?: boolean;
  table_emporter_round_enabled?: boolean;
  dine_in_courses?: unknown[];
}

interface RawDineInCourse {
  key?: unknown;
  label?: unknown;
  category_uuids?: unknown;
}

interface RawCategory {
  uuid: string;
  name: string;
  translations?: CatalogTranslations;
  position: number;
  description: string;
  background_color?: string | null;
  active_lunch?: boolean;
  active_dinner?: boolean;
  available_sur_place?: boolean;
  available_emporter?: boolean;
  available_livraison?: boolean;
  use_custom_product_order?: boolean;
  allow_order_discount?: boolean;
}

interface RawProduct {
  uuid: string;
  /** SC-1280 — absent sur un ancien backend, `null` = non renseigné. */
  allergens?: { contains?: unknown; may_contain?: unknown } | null;
  category: string;
  name: string;
  translations?: CatalogTranslations;
  code?: string;
  position?: number;
  description: string;
  image: string | null;
  price_ttc: string;
  option_groups?: string[];
  option_groups_detail?: ProductOptionGroupDetail[];
  active_lunch?: boolean;
  active_dinner?: boolean;
  available_sur_place?: boolean;
  available_emporter?: boolean;
  available_livraison?: boolean;
  included_in_buffet?: boolean;
  allow_order_discount?: boolean;
  mode_visibility?: 'both' | 'classic' | 'buffet';
  prices?: Array<{
    price_type: 'emporter' | 'livraison' | 'sur_place';
    price_ttc: string;
    tva_code: string | null;
  }>;
}

interface RawOptionItem {
  uuid: string;
  name: string;
  translations?: CatalogTranslations;
  price_delta: string;
  position: number;
  image?: string | null;
  min_quantity?: number;
  max_quantity?: number | null;
}

interface RawOptionGroup {
  uuid: string;
  name: string;
  translations?: CatalogTranslations;
  position: number;
  is_required: boolean;
  min_select: number;
  max_select: number;
  items: RawOptionItem[];
  /** SC-739 — 'cart' : question posée sur la commande entière, jamais sur un
   *  plat. Absent sur les réponses d'avant la fonctionnalité ⇒ 'product'. */
  scope?: 'product' | 'cart';
  applies_to?: CartOptionSaleMode[];
  /** SC-748 — montant marchandise TTC à partir duquel la question est posée.
   *  Absent d'un backend antérieur ⇒ aucune condition, comme '0.00'. */
  min_order_amount?: string;
}

interface ProductOptionItemDetail {
  uuid: string;
  name: string;
  translations?: CatalogTranslations;
  price_delta: string;
  is_active: boolean;
  default_price_delta: string;
  default_is_active: boolean;
  is_overridden: boolean;
  position: number;
  foreign_id: string | null;
  image: string | null;
  min_quantity?: number;
  max_quantity?: number | null;
}

interface ProductOptionGroupDetail {
  uuid: string;
  name: string;
  translations?: CatalogTranslations;
  description: string;
  position: number;
  is_required: boolean;
  min_select: number;
  max_select: number;
  // Group-level active flag (resource_api). Absent on older payloads → treat
  // as active. An inactive group can still be bound to a product, so the menu
  // uses this to drop it. (SC-92 follow-up)
  is_active?: boolean;
  items: ProductOptionItemDetail[];
}

function transformAnnouncement(
  raw: RawActiveAnnouncement | null | undefined,
): RestaurantAnnouncement | null {
  if (!raw) return null;
  const lead = renderTiptapToHtml(raw.body_doc) || undefined;
  return {
    id: raw.uuid,
    headline: raw.headline || undefined,
    lead,
    ctaLabel: raw.cta_label || undefined,
    ctaHref: raw.cta_href || undefined,
    translations: raw.translations ?? undefined,
  };
}

function nullIfEmpty<T extends object>(value: T | null | undefined): T | null {
  if (!value) return null;
  if (Object.keys(value).length === 0) return null;
  return value;
}

const FILE_PRESETS = ['lofi', 'jazz', 'cafe', 'bossa', 'chill', 'festive', 'kawaii', 'jrock', 'ocean'] as const;
type FilePreset = (typeof FILE_PRESETS)[number];

function resolveAmbient(raw: string | null | undefined): AmbientSource | null {
  if (!raw || raw === 'none') return null;
  if (raw === 'koto' || raw === 'jpop') return { kind: 'synth', preset: raw };
  if ((FILE_PRESETS as readonly string[]).includes(raw)) {
    return { kind: 'file', key: raw, src: `/audio/${raw as FilePreset}.mp3` };
  }
  return null;
}

// SC-902 — design §1.2: `[{key, label, category_uuids}, …]`, ordered = ordre
// d'envoi. A malformed entry (missing/non-string `key`/`label`) is dropped
// rather than crashing the whole configuration read; `category_uuids` not an
// array (or absent) degrades to no category yet assigned.
function transformDineInCourses(raw: unknown): DineInCourse[] {
  if (!Array.isArray(raw)) return [];
  const courses: DineInCourse[] = [];
  for (const entry of raw) {
    const c = entry as RawDineInCourse;
    if (typeof c?.key !== 'string' || typeof c?.label !== 'string') continue;
    courses.push({
      key: c.key,
      label: c.label,
      categoryUuids: Array.isArray(c.category_uuids)
        ? c.category_uuids.filter((u): u is string => typeof u === 'string')
        : [],
    });
  }
  return courses;
}

export function transformConfiguration(raw: RawConfiguration): RestaurantConfiguration {
  const modes: OrderMode[] = [
    ...(raw.enable_emporter ? (['takeaway'] as OrderMode[]) : []),
    ...(raw.enable_livraison ? (['delivery'] as OrderMode[]) : []),
  ];
  return {
    modes,
    acceptsReservations: raw.enable_reservation,
    acceptsDineIn: raw.enable_sur_place ?? false,
    prepTimeEmporterMin: raw.prep_time_emporter_min,
    prepTimeLivraisonMin: raw.prep_time_livraison_min,
    marqueeMessages: raw.marquee_messages ?? [],
    marqueeTranslations: raw.marquee_messages_translations ?? undefined,
    ambient: resolveAmbient(raw.ambient_preset),
    deliveryPartners: raw.delivery_partners ?? [],
    promotions: Array.isArray(raw.promotions)
      ? raw.promotions.map((entry): Promotion => {
          const p = entry as Record<string, unknown>;
          return {
            id: String(p.id ?? ''),
            label: String(p.label ?? ''),
            minSubtotal: Number(p.min_subtotal ?? 0),
            discountPercent: Number(p.discount_percent ?? 0),
            appliesTo: (p.applies_to ?? 'both') as PromotionAppliesTo,
            validFrom: (p.valid_from as string | null) ?? null,
            validTo: (p.valid_to as string | null) ?? null,
            active: Boolean(p.active ?? true),
          };
        })
      : [],
    announcement: transformAnnouncement(raw.active_announcement),
    hero: nullIfEmpty(raw.hero) as RestaurantHero | null,
    promo: nullIfEmpty(raw.promo) as RestaurantPromo | null,
    paymentOnlineEnabled: raw.payment_online_enabled ?? true,
    paymentOnlineDescription: raw.payment_online_description ?? '',
    paymentOnsiteEnabled: raw.payment_onsite_enabled ?? true,
    paymentOnsiteDescription: raw.payment_onsite_description ?? '',
    minOrderAmountEmporterCents: minAmountToCents(raw.min_order_amount_emporter),
    minOrderAmountLivraisonCents: minAmountToCents(raw.min_order_amount_livraison),
    dineInCoursesEnabled: raw.dine_in_courses_enabled ?? false,
    tableEmporterRoundEnabled: raw.table_emporter_round_enabled ?? false,
    dineInCourses: transformDineInCourses(raw.dine_in_courses),
  };
}

// SC-554 — le backend renvoie une chaine decimale ; absente (ancien resource_api,
// cache froid) vaut 0 = aucun minimum.
function minAmountToCents(value: string | undefined): number {
  const n = parseFloat(value ?? '0');
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

function priceTtcToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

function priceDeltaToCents(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

interface PaginatedRows<T> {
  current_page: number;
  total_pages: number;
  results: T[];
}

function rowsFromResponse<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in data) {
    return (data as PaginatedRows<T>).results;
  }
  return [];
}

function totalPages(data: unknown): number {
  if (data && typeof data === 'object' && 'total_pages' in data) {
    return Math.max(1, (data as PaginatedRows<unknown>).total_pages);
  }
  return 1;
}

function absolutePhoto(image: string | null): string {
  if (!image) return '';
  if (/^https?:\/\//i.test(image)) return image;
  const base = (import.meta.env.VITE_RESOURCE_API_URL ?? '').replace(/\/$/, '');
  return `${base}${image.startsWith('/') ? '' : '/'}${image}`;
}

function transformOptionGroup(raw: RawOptionGroup): OptionGroup {
  const options: OptionChoice[] = [...raw.items]
    .sort((a, b) => a.position - b.position)
    .map((it) => ({
      id: it.uuid,
      label: it.name,
      ...(it.translations ? { translations: it.translations } : {}),
      priceDelta: priceDeltaToCents(it.price_delta),
      ...(it.image ? { photo: absolutePhoto(it.image) } : {}),
      minQty: it.min_quantity ?? 0,
      maxQty: it.max_quantity ?? null,
    }));
  return {
    id: raw.uuid,
    label: raw.name,
    ...(raw.translations ? { translations: raw.translations } : {}),
    required: raw.is_required,
    multiple: raw.max_select > 1,
    min: raw.min_select,
    max: raw.max_select,
    options,
    ...(raw.applies_to ? { appliesTo: raw.applies_to } : {}),
    minOrderAmount: priceDeltaToCents(raw.min_order_amount ?? '0'),
  };
}

function transformOptionGroupDetail(detail: ProductOptionGroupDetail): OptionGroup {
  const options: OptionChoice[] = [...detail.items]
    .filter((it) => it.is_active)
    .sort((a, b) => a.position - b.position)
    .map((it) => ({
      id: it.uuid,
      label: it.name,
      ...(it.translations ? { translations: it.translations } : {}),
      priceDelta: priceDeltaToCents(it.price_delta),
      ...(it.image ? { photo: absolutePhoto(it.image) } : {}),
      minQty: it.min_quantity ?? 0,
      maxQty: it.max_quantity ?? null,
    }));
  return {
    id: detail.uuid,
    label: detail.name,
    ...(detail.translations ? { translations: detail.translations } : {}),
    required: detail.is_required,
    multiple: detail.max_select > 1,
    min: detail.min_select,
    max: detail.max_select,
    options,
  };
}

function composeMenu(
  uuid: string,
  cats: RawCategory[],
  prods: RawProduct[],
  groups: RawOptionGroup[],
): Menu {
  // All categories are returned; mode-based filtering is done client-side via
  // filterMenuByMode at render time (Part B/C).
  const onlineCats = cats;
  const onlineCatIds = new Set(onlineCats.map((c) => c.uuid));
  const onlineProds = prods.filter((p) => onlineCatIds.has(p.category));
  // An option group deactivated in the admin can stay bound to a product. It
  // must not render — its group-level is_active rides in option_groups_detail,
  // so collect the inactive ones and drop them from both the group list and
  // each product's optionGroupIds.
  //
  // DF-0161 — les retirer ne suffit pas quand le groupe était OBLIGATOIRE : le
  // plat se retrouvait sans aucun groupe et repassait en ajout direct, vendu à
  // plein tarif sans qu'un seul choix soit enregistré (mesuré chez Yuji le
  // 2026-09-04, plat « 3 Inari sushi au choix »). `resource_api` refuse
  // maintenant ce devis ; la carte doit le dire AVANT, plat par plat — d'où
  // `unavailableReason` ci-dessous, calculé sur le détail DU produit pour que
  // la surcharge de rattachement (min_select par produit) soit respectée.
  const inactiveGroupIds = new Set<string>();
  for (const p of onlineProds) {
    for (const g of p.option_groups_detail ?? []) {
      if (g.is_active === false) inactiveGroupIds.add(g.uuid);
    }
  }
  const categories: Category[] = onlineCats.map((c) => ({
    id: c.uuid,
    name: c.name,
    ...(c.translations ? { translations: c.translations } : {}),
    emoji: undefined,
    color: c.background_color || undefined,
    order: c.position,
    description: c.description || undefined,
    active_lunch: c.active_lunch ?? true,
    active_dinner: c.active_dinner ?? true,
    available_sur_place: c.available_sur_place ?? true,
    available_emporter: c.available_emporter ?? true,
    available_livraison: c.available_livraison ?? true,
    useCustomProductOrder: c.use_custom_product_order ?? false,
    allowOrderDiscount: c.allow_order_discount ?? true,
  }));
  // Resolve each product's option groups from ITS OWN option_groups_detail, so
  // per-product overrides (price AND is_active) are respected. The flat
  // Menu.optionGroups built below keys by group-uuid and collapses a shared
  // group to one product's detail — consumers MUST use item.optionGroups, not
  // that shared list, or a product inherits another product's options. (SC-93)
  // SC-739 — les groupes posés sur la commande entière voyagent dans la même
  // liste que ceux des plats (l'endpoint catalogue est commun). On les sort
  // AVANT toute résolution par produit : laissés là, ils tomberaient dans le
  // `Menu.optionGroups` à plat et pourraient s'afficher sous un plat.
  const cartGroupsRaw = groups.filter((g) => g.scope === 'cart');
  const productGroups = groups.filter((g) => g.scope !== 'cart');
  const standaloneByUuid = new Map(productGroups.map((g) => [g.uuid, g]));
  const items: MenuItem[] = onlineProds.map((p) => {
    const detailForProduct = new Map(
      (p.option_groups_detail ?? []).map((g) => [g.uuid, g]),
    );
    const optionGroupIds = (p.option_groups ?? []).filter(
      (id) => !inactiveGroupIds.has(id),
    );
    // DF-0161 — un groupe désactivé dont ce produit exige au moins un choix.
    const requiredGroupInactive = (p.option_groups_detail ?? []).some(
      (g) => g.is_active === false && (g.is_required || g.min_select >= 1),
    );
    const optionGroups = optionGroupIds
      .map((id) => {
        const detail = detailForProduct.get(id);
        if (detail) return transformOptionGroupDetail(detail);
        const raw = standaloneByUuid.get(id);
        return raw ? transformOptionGroup(raw) : undefined;
      })
      // Drop groups whose options are all inactive for this product — an empty
      // (esp. required) group is unselectable and would deadlock the modal.
      .filter((g): g is OptionGroup => g !== undefined && g.options.length > 0);
    const pricesByMode = (p.prices ?? []).reduce<Partial<Record<'emporter' | 'livraison' | 'sur_place', number>>>(
      (acc, pr) => { acc[pr.price_type] = priceTtcToCents(pr.price_ttc); return acc; },
      {},
    );
    return {
      id: p.uuid,
      categoryId: p.category,
      name: p.name,
      ...(p.translations ? { translations: p.translations } : {}),
      ...(parseProductAllergens(p.allergens) ? { allergens: parseProductAllergens(p.allergens) } : {}),
      code: p.code,
      position: p.position ?? 0,
      description: p.description,
      photo: absolutePhoto(p.image),
      price: priceTtcToCents(p.price_ttc),
      badges: [],
      hot: false,
      optionGroupIds,
      optionGroups,
      active_lunch: p.active_lunch ?? true,
      active_dinner: p.active_dinner ?? true,
      available_sur_place: p.available_sur_place ?? true,
      available_emporter: p.available_emporter ?? true,
      available_livraison: p.available_livraison ?? true,
      includedInBuffet: p.included_in_buffet ?? false,
      allowOrderDiscount: p.allow_order_discount ?? true,
      modeVisibility: p.mode_visibility ?? 'both',
      ...(requiredGroupInactive
        ? { unavailableReason: 'requiredOptionGroupInactive' as const }
        : {}),
      ...(Object.keys(pricesByMode).length > 0 ? { pricesByMode } : {}),
    };
  });
  // Legacy flat Menu.optionGroups: one OptionGroup per uuid, first-seen detail
  // wins when products diverge. The per-product correct source is now
  // item.optionGroups (above); this list is kept for backward compatibility and
  // must NOT be used to render a specific product's options. (SC-93)
  const detailByUuid = new Map<string, ProductOptionGroupDetail>();
  for (const p of onlineProds) {
    for (const g of p.option_groups_detail ?? []) {
      if (inactiveGroupIds.has(g.uuid)) continue;
      if (!detailByUuid.has(g.uuid)) detailByUuid.set(g.uuid, g);
    }
  }
  const sortedGroups = [...productGroups].sort((a, b) => a.position - b.position);
  const optionGroups: OptionGroup[] = sortedGroups.map((g) => {
    const detail = detailByUuid.get(g.uuid);
    return detail ? transformOptionGroupDetail(detail) : transformOptionGroup(g);
  });
  // Defensive: include detail-only groups absent from the standalone list.
  const seen = new Set(optionGroups.map((g) => g.id));
  for (const [uuid, detail] of detailByUuid) {
    if (!seen.has(uuid)) optionGroups.push(transformOptionGroupDetail(detail));
  }
  // SC-739 — les questions posées au moment de valider, dans l'ordre configuré.
  // Un groupe sans réponse active n'est pas renvoyé : il ne pourrait rien
  // demander et une question obligatoire bloquerait la validation pour rien.
  const cartOptionGroups: OptionGroup[] = [...cartGroupsRaw]
    .sort((a, b) => a.position - b.position)
    .map(transformOptionGroup)
    .filter((g) => g.options.length > 0);
  return { restaurantUuid: uuid, categories, items, optionGroups, cartOptionGroups };
}

export function filterMenuByPeriod(menu: Menu, period: Period): Menu {
  if (period === 'closed') return menu;
  const flag = period === 'lunch' ? 'active_lunch' : 'active_dinner';
  const cats = menu.categories.filter((c) => c[flag]);
  const items = menu.items.filter((i) => {
    const cat = cats.find((c) => c.id === i.categoryId);
    return Boolean(cat) && i[flag];
  });
  const withItems = new Set(items.map((i) => i.categoryId));
  return {
    ...menu,
    categories: cats.filter((c) => withItems.has(c.id)),
    items,
  };
}

export type MenuFilterMode = 'sur_place' | 'emporter' | 'livraison';

export function filterMenuByMode(menu: Menu, mode: MenuFilterMode): Menu {
  const flag = `available_${mode}` as 'available_sur_place' | 'available_emporter' | 'available_livraison';
  const cats = menu.categories.filter((c) => c[flag]);
  const items = menu.items.filter((i) => {
    const cat = cats.find((c) => c.id === i.categoryId);
    return Boolean(cat) && i[flag];
  });
  return { ...menu, categories: cats, items };
}

export type ServiceMode = 'classic' | 'buffet';

// SC-237: hide products that are not present in the current service mode. The
// context is the buffet dine-in session (selectIsBuffetDineIn) vs everything
// else (classic). Only products carry mode_visibility; categories left empty
// after the filter are dropped (same cleanup as filterMenuByPeriod). A missing
// modeVisibility is treated as 'both', so existing menus are unchanged.
export function filterMenuByServiceMode(menu: Menu, ctx: ServiceMode): Menu {
  const items = menu.items.filter((i) => {
    const v = i.modeVisibility ?? 'both';
    return v === 'both' || v === ctx;
  });
  const withItems = new Set(items.map((i) => i.categoryId));
  return { ...menu, categories: menu.categories.filter((c) => withItems.has(c.id)), items };
}

export const resourceApi = createApi({
  reducerPath: 'resourceApi',
  baseQuery: withLoadResilience(
    fetchBaseQuery({
      baseUrl: import.meta.env.VITE_RESOURCE_API_URL,
    }),
  ),
  tagTypes: ['Configuration', 'Menu', 'Order', 'Reservation'],
  endpoints: (build) => ({
    getConfiguration: build.query<RestaurantConfiguration, string>({
      query: (uuid) => `/resource/configuration/public/${uuid}/`,
      transformResponse: transformConfiguration,
      providesTags: (_r, _e, uuid) => [{ type: 'Configuration', id: uuid }],
    }),
    getMenu: build.query<Menu, string>({
      async queryFn(arg, _api, _opts, baseQuery) {
        const uuid = arg;
        const PAGE_SIZE = 100;
        const fetchAll = async <T>(path: string, extraQuery = '') => {
          const first = await baseQuery(
            `${path}?restaurant_uuid=${uuid}&page=1&page_size=${PAGE_SIZE}${extraQuery}`,
          );
          if (first.error) return { rows: [] as T[], error: first.error };
          const pages = totalPages(first.data);
          const collected = rowsFromResponse<T>(first.data);
          if (pages > 1) {
            const rest = await Promise.all(
              Array.from({ length: pages - 1 }, (_, i) =>
                baseQuery(`${path}?restaurant_uuid=${uuid}&page=${i + 2}&page_size=${PAGE_SIZE}${extraQuery}`),
              ),
            );
            for (const r of rest) {
              if (r.error) return { rows: [] as T[], error: r.error };
              collected.push(...rowsFromResponse<T>(r.data));
            }
          }
          return { rows: collected, error: undefined };
        };
        const [cats, prods, groups] = await Promise.all([
          // Active-only across the board: the catalog list endpoints return
          // inactive rows by default (the include_inactive filter is skipped
          // when the param is absent). Inactive products fail the order quote
          // with "Unknown or inactive product"; inactive categories/groups
          // must not surface either. Request active-only explicitly. (SC-92)
          fetchAll<RawCategory>('/resource/catalog/category/', '&include_inactive=false'),
          fetchAll<RawProduct>('/resource/catalog/product/', '&include_inactive=false'),
          fetchAll<RawOptionGroup>('/resource/catalog/option_group/', '&include_inactive=false'),
        ]);
        if (cats.error) return { error: cats.error };
        if (prods.error) return { error: prods.error };
        if (groups.error) return { error: groups.error };
        return { data: composeMenu(uuid, cats.rows, prods.rows, groups.rows) };
      },
      providesTags: (_r, _e, uuid) => [{ type: 'Menu', id: uuid }],
    }),
  }),
});

export const { useGetConfigurationQuery, useGetMenuQuery } = resourceApi;
